import {
  type Icons,
  useCallOptions,
  Label as LabelClient,
  useLabelClient,
  withBearerToken,
  timestampToDateOptional,
  Label as CpLabel,
} from "@storytell/cp-client";
import { type Component, createMemo, createSignal } from "solid-js";
import { useWire } from "~/wire";
import { LabelPicker } from "./LabelPicker";
import type { AssetId, Label, LabelId } from "@storytell/types";
import { iconEnumToString } from "@storytell/bsky-icons";

export const AssetLabelPicker: Component<{
  assetId: string;
  assetLabels: Label[];
  onLabelCreated: (label: LabelClient.Label) => void;
  mount?: Node;
}> = (props) => {
  const wire = useWire();
  const [search, setSearch] = createSignal("");

  const labels = createMemo(() => wire.sync.labels.search(search()));

  const client = useLabelClient();
  const onCreateLabel = async (data: {
    name: string;
    description: string;
    icon: Icons.Icon;
    color: string;
  }) => {
    try {
      const callOptions = useCallOptions(withBearerToken(wire.auth.token()));
      const result = await client.createLabel(
        {
          displayName: data.name,
          description: data.description,
          color: data.color,
          icon: data.icon,
          scope: LabelClient.LabelScope.PROJECT,
          organizationId: wire.auth.realm()?.organizationId,
          projectId: wire.auth.realm()?.projectId,
          workspaceId: wire.auth.realm()?.workspaceId,
          tenantId: wire.auth.realm()?.tenantId,
        },
        callOptions,
      );

      if (result.isOk()) {
        const label = result.value.label;
        wire.sync.labels.set(label.id as LabelId, {
          id: label.id as LabelId,
          name: label.displayName,
          description: label.description,
          icon: iconEnumToString(label.icon),
          color: data.color,
          scope:
            Object.entries(CpLabel.LabelScope).find(([, value]) => value === label.scope)?.[0] || "LABEL_SCOPE_PROJECT",
          usageCount: Number(label.usageCount),
          lastUsedAt: timestampToDateOptional(label.lastUsedAt)?.toISOString(),
          tenantId: label.tenantId,
          organizationId: label.organizationId || "",
          workspaceId: label.workspaceId || "",
          projectId: label.projectId || "",
          createdBy: label.auditMetadata?.created.userId || "",
          createdAt: timestampToDateOptional(label.createdAt)?.toISOString() ?? new Date().toISOString(),
          modifiedBy: label.auditMetadata?.modified.userId || "",
          modifiedAt: timestampToDateOptional(label.updatedAt)?.toISOString() ?? new Date().toISOString(),
          isArchived: false,
          userId: label.auditMetadata?.created.userId || "",
        });
        await onAddLabelToAsset(result.value.label.id as LabelId);
        props.onLabelCreated(result.value.label);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onAddLabelToAsset = async (labelId: LabelId) => {
    wire.sync.addLabelToAsset(props.assetId as AssetId, labelId);

    try {
      const result = await client.associateLabel(
        {
          labelId,
          resourceId: props.assetId,
          resourceType: "asset",
        },
        useCallOptions(withBearerToken(wire.auth.token())),
      );

      if (result.isErr()) {
        wire.sync.removeLabelFromAsset(props.assetId as AssetId, labelId);
        return;
      }
    } catch (error) {
      console.error(error);
      wire.sync.removeLabelFromAsset(props.assetId as AssetId, labelId);
    }
  };

  const onRemoveLabelFromAsset = async (labelId: LabelId) => {
    const existing = wire.sync.getAssetHasLabel(props.assetId as AssetId, labelId);
    if (!existing) return;

    wire.sync.removeLabelFromAsset(props.assetId as AssetId, labelId);
    try {
      const result = await client.disassociateLabel(
        {
          labelId,
          resourceId: props.assetId,
          resourceType: "asset",
        },
        useCallOptions(withBearerToken(wire.auth.token())),
      );

      if (result.isErr()) {
        wire.sync.addLabelToAsset(props.assetId as AssetId, labelId);
        return;
      }
    } catch (error) {
      console.error(error);
      wire.sync.addLabelToAsset(props.assetId as AssetId, labelId);
    }
  };

  return (
    <LabelPicker
      search={search()}
      setSearch={setSearch}
      labels={labels()}
      selectedLabels={props.assetLabels.map((l) => l.id)}
      onSelectLabel={(label) => onAddLabelToAsset(label.id)}
      onDeselectLabel={(label) => onRemoveLabelFromAsset(label.id)}
      onCreateLabel={onCreateLabel}
      mount={props.mount}
    />
  );
};
